import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Trash2, Search, X, Plus, Archive, Package, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const apiFetch = async (path, opts={}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`}, ...opts
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
};

// ── Step 1: choose Scrap or Disposal ────────────────────────────────────────
function ActionTypeChoice({ onChoose, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Remove an Asset</h2>
          <button className="btn btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:18 }}>
            How is this asset leaving active inventory?
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <button
              type="button"
              onClick={() => onChoose('scrap')}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 18px', borderRadius:'var(--radius)', border:'1px solid rgba(248,113,113,.3)', background:'var(--red-bg)', cursor:'pointer', textAlign:'left' }}
            >
              <Trash2 size={22} style={{ color:'var(--red)', flexShrink:0 }}/>
              <div>
                <div style={{ fontWeight:700, color:'var(--red)', fontSize:14 }}>Scrap</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Beyond repair, kept on-site as scrap</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChoose('disposal')}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 18px', borderRadius:'var(--radius)', border:'1px solid var(--border)', background:'var(--surface2)', cursor:'pointer', textAlign:'left' }}
            >
              <Package size={22} style={{ color:'#64748b', flexShrink:0 }}/>
              <div>
                <div style={{ fontWeight:700, color:'#64748b', fontSize:14 }}>Disposal</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Handed off to a disposal / recycling vendor</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Scrap form ──────────────────────────────────────────────────────
function AddScrapModal({ assets, onClose, onBack, onSaved }) {
  const [assetId, setAssetId] = useState('');
  const [reason,  setReason]  = useState('');
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const availableAssets = assets.filter(a => a.status !== 'Allocated' && a.status !== 'Scrap' && a.status !== 'Disposal');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/scraps', { method:'POST', body: JSON.stringify({ asset_id: assetId, reason, notes }) });
      onSaved(); onClose();
    } catch(err){ alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button type="button" className="btn btn-icon btn-sm" onClick={onBack}><ArrowLeft size={15}/></button>
            <h2 className="modal-title">Scrap an Asset</h2>
          </div>
          <button className="btn btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Select Asset *</label>
              <select className="form-select" required value={assetId} onChange={e=>setAssetId(e.target.value)}>
                <option value="">-- Choose asset --</option>
                {availableAssets.map(a=>(
                  <option key={a.id} value={a.id}>{a.id} — {a.brand} {a.model} ({a.status})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reason for Scrapping *</label>
              <textarea className="form-textarea" required value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Motherboard failure — beyond economical repair"/>
            </div>
            <div className="form-group">
              <label className="form-label">Additional Notes</label>
              <textarea className="form-textarea" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any additional details…"/>
            </div>
            <div style={{padding:'10px 14px',background:'var(--red-bg)',border:'1px solid rgba(248,113,113,.2)',borderRadius:'var(--radius)',fontSize:13,color:'var(--red)',marginBottom:16}}>
              <strong>Warning:</strong> This will permanently mark the asset as Scrap and remove it from active inventory.
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-danger" disabled={saving}>
                <Trash2 size={14}/> {saving?'Scrapping…':'Confirm Scrap'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Disposal form ───────────────────────────────────────────────────
function AddDisposalModal({ assets, onClose, onBack, onSaved }) {
  const [assetId,      setAssetId]      = useState('');
  const [vendor,       setVendor]       = useState('');
  const [reason,       setReason]       = useState('');
  const [notes,        setNotes]        = useState('');
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving,       setSaving]       = useState(false);

  const availableAssets = assets.filter(a => a.status !== 'Allocated' && a.status !== 'Scrap' && a.status !== 'Disposal');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/disposals', {
        method:'POST',
        body: JSON.stringify({ asset_id: assetId, vendor, reason, notes, disposal_date: disposalDate })
      });
      onSaved(); onClose();
    } catch(err){ alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button type="button" className="btn btn-icon btn-sm" onClick={onBack}><ArrowLeft size={15}/></button>
            <h2 className="modal-title">Dispose an Asset</h2>
          </div>
          <button className="btn btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Select Asset *</label>
              <select className="form-select" required value={assetId} onChange={e=>setAssetId(e.target.value)}>
                <option value="">-- Choose asset --</option>
                {availableAssets.map(a=>(
                  <option key={a.id} value={a.id}>{a.id} — {a.brand} {a.model} ({a.status})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Vendor *</label>
              <input className="form-input" required value={vendor} onChange={e=>setVendor(e.target.value)} placeholder="e.g. GreenTech Recyclers"/>
            </div>
            <div className="form-group">
              <label className="form-label">Disposal Date</label>
              <input type="date" className="form-input" value={disposalDate} onChange={e=>setDisposalDate(e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Reason for Disposal *</label>
              <textarea className="form-textarea" required value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. End of life, obsolete hardware"/>
            </div>
            <div className="form-group">
              <label className="form-label">Additional Notes</label>
              <textarea className="form-textarea" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any additional details…"/>
            </div>
            <div style={{padding:'10px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:13,color:'var(--text-dim)',marginBottom:16}}>
              <strong>Note:</strong> This will mark the asset as Disposed and remove it from active inventory.
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ background:'#64748b', borderColor:'#64748b' }}>
                <Package size={14}/> {saving?'Disposing…':'Confirm Disposal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function ScrapAssets() {
  const { scraps, disposals, assets, refetch } = useApp();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab,        setTab]        = useState('scrap'); // 'scrap' | 'disposal'
  const [search,      setSearch]     = useState('');
  const [addStep,     setAddStep]    = useState(null);    // null | 'choose' | 'scrap' | 'disposal'
  const [deleteConf,  setDeleteConf] = useState(null);     // { kind:'scrap'|'disposal', ...record }
  const [deleting,    setDeleting]   = useState(false);

  const disposalList = disposals || [];

  const filteredScraps = scraps.filter(s =>
    !search || [s.assetId, s.serial, s.model, s.reason, s.brand]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );
  const filteredDisposals = disposalList.filter(d =>
    !search || [d.assetId, d.serial, d.model, d.reason, d.brand, d.vendor]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const getAsset = id => assets.find(a => a.id === id);

  const handleDelete = async () => {
    if (!deleteConf) return;
    setDeleting(true);
    try {
      const path = deleteConf.kind === 'scrap' ? `/scraps/${deleteConf.dbId}` : `/disposals/${deleteConf.dbId}`;
      await apiFetch(path, { method:'DELETE' });
      setDeleteConf(null);
      refetch();
    } catch(err){ alert(err.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fade-in">
      <div className="page-header page-header-row">
        <div>
          <h1>Scrap &amp; Disposal</h1>
          <p>End-of-life laptops removed from active inventory</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{padding:'8px 14px',background:'var(--red-bg)',border:'1px solid rgba(248,113,113,.2)',borderRadius:'var(--radius)',fontSize:13,color:'var(--red)',fontWeight:600}}>
            <Trash2 size={14} style={{display:'inline',marginRight:6}}/>
            {scraps.length} Scrapped
          </div>
          <div style={{padding:'8px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:13,color:'#64748b',fontWeight:600}}>
            <Package size={14} style={{display:'inline',marginRight:6}}/>
            {disposalList.length} Disposed
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={()=>setAddStep('choose')}>
              <Plus size={14}/> Remove Asset
            </button>
          )}
        </div>
      </div>

      <div className="toggle-group" style={{ marginBottom:16 }}>
        <button className={`toggle-btn ${tab==='scrap'?'active':''}`} onClick={() => { setTab('scrap'); setSearch(''); }}>
          <Trash2 size={13} style={{ marginRight:5, verticalAlign:'middle' }}/> Scrap Assets
        </button>
        <button className={`toggle-btn ${tab==='disposal'?'active':''}`} onClick={() => { setTab('disposal'); setSearch(''); }}>
          <Package size={13} style={{ marginRight:5, verticalAlign:'middle' }}/> Disposal Assets
        </button>
      </div>

      <div className="inv-toolbar">
        <div className="search-bar" style={{flex:1,maxWidth:340}}>
          <Search size={15}/>
          <input
            className="form-input"
            placeholder={tab==='scrap' ? 'Search asset, model, reason…' : 'Search asset, model, vendor, reason…'}
            value={search}
            onChange={e=>setSearch(e.target.value)}
          />
        </div>
      </div>

      {tab === 'scrap' ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Scrap ID</th><th>Asset No</th><th>Serial</th>
                <th>Model</th><th>Scrap Date</th><th>Reason</th><th>Approved By</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredScraps.length===0 ? (
                <tr><td colSpan={isAdmin?8:7}><div className="empty-state"><Trash2 size={32}/><p>No scrap records found</p></div></td></tr>
              ) : filteredScraps.map(s => {
                const asset = getAsset(s.assetId);
                return (
                  <tr key={s.id}>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-muted)'}}>{s.id}</span></td>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12.5,color:'var(--red)',fontWeight:700}}>{s.assetId}</span></td>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12}}>{s.serial||asset?.serial||'—'}</span></td>
                    <td style={{fontSize:12.5,color:'var(--text-dim)'}}>{asset?.brand||s.brand||'—'} {s.model||asset?.model||''}</td>
                    <td style={{fontFamily:'var(--mono)',fontSize:12}}>{s.scrapDate}</td>
                    <td style={{fontSize:13,maxWidth:200,color:'var(--text-dim)'}}>{s.reason}</td>
                    <td style={{fontSize:12.5}}>{s.approvedBy}</td>
                    {isAdmin && (
                      <td>
                        <button className="btn btn-sm btn-danger" onClick={()=>setDeleteConf({ kind:'scrap', dbId:s.dbId ?? s.id, id:s.id, assetId:s.assetId })}>
                          <Trash2 size={12}/> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Disposal ID</th><th>Asset No</th><th>Serial</th>
                <th>Model</th><th>Vendor</th><th>Disposal Date</th><th>Reason</th><th>Approved By</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredDisposals.length===0 ? (
                <tr><td colSpan={isAdmin?9:8}><div className="empty-state"><Package size={32}/><p>No disposal records found</p></div></td></tr>
              ) : filteredDisposals.map(d => {
                const asset = getAsset(d.assetId);
                return (
                  <tr key={d.id}>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-muted)'}}>{d.id}</span></td>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12.5,color:'#64748b',fontWeight:700}}>{d.assetId}</span></td>
                    <td><span style={{fontFamily:'var(--mono)',fontSize:12}}>{d.serial||asset?.serial||'—'}</span></td>
                    <td style={{fontSize:12.5,color:'var(--text-dim)'}}>{asset?.brand||d.brand||'—'} {d.model||asset?.model||''}</td>
                    <td style={{fontSize:12.5,fontWeight:600}}>{d.vendor||'—'}</td>
                    <td style={{fontFamily:'var(--mono)',fontSize:12}}>{d.disposalDate}</td>
                    <td style={{fontSize:13,maxWidth:200,color:'var(--text-dim)'}}>{d.reason}</td>
                    <td style={{fontSize:12.5}}>{d.approvedBy}</td>
                    {isAdmin && (
                      <td>
                        <button className="btn btn-sm btn-danger" onClick={()=>setDeleteConf({ kind:'disposal', dbId:d.dbId ?? d.id, id:d.id, assetId:d.assetId })}>
                          <Trash2 size={12}/> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {addStep === 'choose' && (
        <ActionTypeChoice onChoose={(type) => setAddStep(type)} onClose={() => setAddStep(null)} />
      )}
      {addStep === 'scrap' && (
        <AddScrapModal assets={assets} onClose={()=>setAddStep(null)} onBack={()=>setAddStep('choose')} onSaved={refetch}/>
      )}
      {addStep === 'disposal' && (
        <AddDisposalModal assets={assets} onClose={()=>setAddStep(null)} onBack={()=>setAddStep('choose')} onSaved={refetch}/>
      )}

      {deleteConf && (
        <div className="modal-overlay" onClick={()=>setDeleteConf(null)}>
          <div className="modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Delete {deleteConf.kind === 'scrap' ? 'Scrap' : 'Disposal'} Record</h2>
              <button className="btn btn-icon" onClick={()=>setDeleteConf(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{padding:'14px 16px',background:'var(--red-bg)',border:'1px solid rgba(248,113,113,.2)',borderRadius:'var(--radius)',marginBottom:20}}>
                <div style={{fontWeight:600,color:'var(--red)',marginBottom:4}}>Delete {deleteConf.kind} record {deleteConf.id}?</div>
                <div style={{fontSize:13,color:'var(--text-muted)'}}>
                  This removes the {deleteConf.kind} record for asset <strong>{deleteConf.assetId}</strong>. Its inventory status will need to be updated separately if it should return to Stock.
                </div>
              </div>
              <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={()=>setDeleteConf(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                  <Trash2 size={14}/> {deleting?'Deleting…':'Delete Record'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
